import { useState, useCallback } from 'react'
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, uploadBytes } from 'firebase/storage'
import { Timestamp } from 'firebase/firestore'
import { storage } from '@/core/firebase'
import { generateThumbnail, getThumbnailFilename } from '@/lib/imageUtils'
import type { Media } from '@/types'

export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'failed'

export interface UploadingMedia {
  id: string
  file: File
  previewUrl: string
  type: 'image' | 'video' | 'document'
  status: UploadStatus
  progress: number
  error?: string
  media?: Media // populated when completed
}

interface UseMediaUploadOptions {
  basePath: string // e.g., 'exercise_media'
  trainerId: string
  generateThumbnails?: boolean // default true
}

export function useMediaUpload({ basePath, trainerId, generateThumbnails = true }: UseMediaUploadOptions) {
  const [uploads, setUploads] = useState<UploadingMedia[]>([])

  const updateUpload = useCallback((id: string, updates: Partial<UploadingMedia>) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...updates } : u)))
  }, [])

  const uploadFile = useCallback(
    async (file: File): Promise<Media | null> => {
      const id = crypto.randomUUID()
      const mediaId = crypto.randomUUID()
      const type = file.type.startsWith('video')
        ? 'video'
        : file.type === 'application/pdf'
          ? 'document'
          : 'image'

      const previewUrl = URL.createObjectURL(file)

      // Add to uploads list immediately
      const uploadEntry: UploadingMedia = {
        id,
        file,
        previewUrl,
        type,
        status: 'uploading',
        progress: 0,
      }
      setUploads((prev) => [...prev, uploadEntry])

      // Storage paths
      const storagePath = `${basePath}/${trainerId}/${mediaId}-${file.name}`
      const storageRef = ref(storage, storagePath)

      // Generate thumbnail for images (non-blocking)
      let thumbnailPromise: Promise<{ path: string; url: string } | null> = Promise.resolve(null)
      if (type === 'image' && generateThumbnails) {
        thumbnailPromise = (async () => {
          try {
            const thumbnailBlob = await generateThumbnail(file)
            if (!thumbnailBlob) return null

            const thumbFilename = getThumbnailFilename(file.name)
            const thumbnailPath = `${basePath}/${trainerId}/thumbs/${mediaId}-${thumbFilename}`
            const thumbRef = ref(storage, thumbnailPath)

            // Upload thumbnail (fire and forget, doesn't affect main progress)
            await uploadBytes(thumbRef, thumbnailBlob)
            const thumbnailUrl = await getDownloadURL(thumbRef)

            return { path: thumbnailPath, url: thumbnailUrl }
          } catch (error) {
            console.error('Thumbnail generation failed:', error)
            return null
          }
        })()
      }

      return new Promise((resolve) => {
        const uploadTask = uploadBytesResumable(storageRef, file)

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            updateUpload(id, { progress })
          },
          (error) => {
            updateUpload(id, { status: 'failed', error: error.message })
            resolve(null)
          },
          async () => {
            try {
              const [downloadUrl, thumbnail] = await Promise.all([
                getDownloadURL(uploadTask.snapshot.ref),
                thumbnailPromise,
              ])

              const media: Media = {
                mediaId,
                storagePath,
                downloadUrl,
                type,
                caption: '',
                createdAt: Timestamp.now(),
                ...(thumbnail && {
                  thumbnailPath: thumbnail.path,
                  thumbnailUrl: thumbnail.url,
                }),
              }
              updateUpload(id, { status: 'completed', progress: 100, media })
              resolve(media)
            } catch {
              updateUpload(id, { status: 'failed', error: 'Failed to get download URL' })
              resolve(null)
            }
          }
        )
      })
    },
    [basePath, trainerId, generateThumbnails, updateUpload]
  )

  const uploadFiles = useCallback(
    async (files: FileList | File[]): Promise<Media[]> => {
      const fileArray = Array.from(files)
      const results = await Promise.all(fileArray.map(uploadFile))
      return results.filter((m): m is Media => m !== null)
    },
    [uploadFile]
  )

  const deleteMedia = useCallback(async (media: Media): Promise<boolean> => {
    if (!media.storagePath) return true // External URL, nothing to delete

    const deletePromises: Promise<void>[] = []

    // Delete main file
    deletePromises.push(
      deleteObject(ref(storage, media.storagePath)).catch((err) =>
        console.error('Failed to delete main file:', err)
      )
    )

    // Delete thumbnail if exists
    if (media.thumbnailPath) {
      deletePromises.push(
        deleteObject(ref(storage, media.thumbnailPath)).catch((err) =>
          console.error('Failed to delete thumbnail:', err)
        )
      )
    }

    try {
      await Promise.all(deletePromises)
      return true
    } catch (error) {
      console.error('Failed to delete media from storage:', error)
      return false
    }
  }, [])

  const removeUpload = useCallback((id: string) => {
    setUploads((prev) => {
      const upload = prev.find((u) => u.id === id)
      if (upload?.previewUrl) {
        URL.revokeObjectURL(upload.previewUrl)
      }
      return prev.filter((u) => u.id !== id)
    })
  }, [])

  const retryUpload = useCallback(
    async (id: string): Promise<Media | null> => {
      const upload = uploads.find((u) => u.id === id)
      if (!upload || upload.status !== 'failed') return null

      // Remove failed upload
      removeUpload(id)

      // Re-upload the file
      return uploadFile(upload.file)
    },
    [uploads, removeUpload, uploadFile]
  )

  const clearCompleted = useCallback(() => {
    setUploads((prev) => {
      prev.filter((u) => u.status === 'completed').forEach((u) => {
        if (u.previewUrl) URL.revokeObjectURL(u.previewUrl)
      })
      return prev.filter((u) => u.status !== 'completed')
    })
  }, [])

  const isUploading = uploads.some((u) => u.status === 'uploading')
  const hasFailed = uploads.some((u) => u.status === 'failed')
  const completedMedia = uploads
    .filter((u) => u.status === 'completed' && u.media)
    .map((u) => u.media!)

  return {
    uploads,
    uploadFiles,
    uploadFile,
    deleteMedia,
    removeUpload,
    retryUpload,
    clearCompleted,
    isUploading,
    hasFailed,
    completedMedia,
  }
}
